import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useCustomer } from '../auth/CustomerContext';
import { formatCzk } from '../money';
import { usePageMeta } from '../seo';
import {
  AffiliateError,
  affiliateApi,
  type CommissionStatus,
  type PartnerOverview,
} from '../affiliate/api';

/**
 * Partnerský program — přehled pro partnera.
 *
 * Otevírá se přihlášeným zákazníkem, ke kterému obsluha připojila partnera.
 * Vidí SVŮJ odkaz, SVOJE prokliky, SVOJE provize a SVOJE výplaty. Nic
 * o zákaznících: ani e-mail, ani jméno, ani číslo objednávky — to jsou
 * údaje obchodu a jeho zákazníka, ne partnera. Server vydá jen datum,
 * částky a stav; stránka nemá co víc zobrazit, ani kdyby chtěla.
 */

const dateFmt = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

const STATUS_LABEL: Record<CommissionStatus, string> = {
  pending: 'Čeká na splatnost',
  approved: 'Splatná',
  cancelled: 'Zrušená',
  paid: 'Vyplacená',
};

export function PartnerPage() {
  usePageMeta({ title: 'Partnerský program | Super Krmivo', index: false });
  const { customer, loading } = useCustomer();

  const [data, setData] = useState<PartnerOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notPartner, setNotPartner] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!customer) return;
    let alive = true;
    affiliateApi
      .me()
      .then((result) => {
        if (alive) setData(result);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        if (caught instanceof AffiliateError && caught.status === 404) {
          setNotPartner(true);
          return;
        }
        setError(
          caught instanceof AffiliateError ? caught.message : 'Přehled se nepodařilo načíst.',
        );
      });
    return () => {
      alive = false;
    };
  }, [customer]);

  if (loading && customer === undefined) {
    return (
      <div className="account-page narrow">
        <p className="muted">Načítám…</p>
      </div>
    );
  }

  if (!customer) return <Navigate to="/registrace" replace />;

  if (notPartner) {
    return (
      <div className="account-page narrow">
        <h1>Partnerský program</h1>
        <p className="muted">
          K tomuhle účtu zatím žádný partnerský program připojený není. Chceš o nás psát nebo nás
          doporučovat? Napiš nám a domluvíme se — dostaneš vlastní odkaz i kód a provizi z každé
          objednávky, kterou přivedeš.
        </p>
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="account-page narrow">
        <h1>Partnerský program</h1>
        <p className="alert">{error}</p>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="account-page narrow">
        <p className="muted">Načítám…</p>
      </div>
    );
  }

  async function copyLink(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2_000);
    } catch {
      // Bez schránky si odkaz označí ručně – je celý vidět.
    }
  }

  const rate = (data.partner.commissionBp / 100).toLocaleString('cs-CZ', {
    maximumFractionDigits: 2,
  });

  return (
    <div className="account-page narrow">
      <h1>Partnerský program</h1>

      {data.partner.status !== 'active' && (
        <p className="alert">
          Tvůj partnerský účet je momentálně pozastavený, nové objednávky se nepřipisují. Už
          přiřazené provize doběhnou k výplatě normálně.
        </p>
      )}

      <section className="card">
        <h2>Tvůj odkaz</h2>
        <p className="partner-link">
          <code>{data.partner.link}</code>
        </p>
        <button type="button" className="button" onClick={() => void copyLink(data.partner.link)}>
          {copied ? 'Zkopírováno' : 'Zkopírovat odkaz'}
        </button>
        <p className="muted small">
          Kdo přijde přes tenhle odkaz a do {data.policy.attributionDays} dnů nakoupí, počítá se
          tobě. Rozhoduje poslední odkaz před objednáním. Kód <strong>{data.partner.code}</strong>{' '}
          funguje i samostatně — objednávka s ním se ti připíše, i kdyby zákazník na odkaz nikdy
          neklikl (třeba když si nezapne měření v cookie liště).
        </p>
        <p className="muted small">
          Provize je <strong>{rate} %</strong> z ceny zboží po slevách, bez DPH. Doprava ani
          dobírka se do ní nepočítají — to nejsou naše tržby, jen přeúčtované náklady. Splatná je{' '}
          {data.policy.holdDays} dnů po odeslání zásilky, až doběhne lhůta na vrácení. Vrácená
          nebo zrušená objednávka provizi ruší.
        </p>
      </section>

      <section className="card partner-stats">
        <div>
          <span className="muted small">Prokliky (30 dní)</span>
          <strong>{data.clicks.recent}</strong>
        </div>
        <div>
          <span className="muted small">Objednávky</span>
          <strong>{data.totals.orderCount}</strong>
        </div>
        <div>
          <span className="muted small">Čeká na splatnost</span>
          <strong>{formatCzk(data.totals.pendingMinor)}</strong>
        </div>
        <div>
          <span className="muted small">Splatné</span>
          <strong>{formatCzk(data.totals.approvedMinor)}</strong>
        </div>
        <div>
          <span className="muted small">Vyplaceno</span>
          <strong>{formatCzk(data.totals.paidMinor)}</strong>
        </div>
      </section>

      {data.totals.openClawbackMinor > 0 && (
        <p className="notice">
          Za už vyplacené objednávky, které se mezitím vrátily, se z příští výplaty odečte{' '}
          {formatCzk(data.totals.openClawbackMinor)}.
        </p>
      )}

      <section className="card">
        <h2>Provize</h2>
        {data.commissions.length === 0 ? (
          <p className="muted">
            Zatím žádná. Jakmile někdo přes tvůj odkaz nebo kód nakoupí, objeví se tu.
          </p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Objednáno</th>
                <th>Zdroj</th>
                <th>Stav</th>
                <th className="num">Základ</th>
                <th className="num">Provize</th>
              </tr>
            </thead>
            <tbody>
              {data.commissions.map((commission) => (
                <tr key={commission.id}>
                  <td>{dateFmt.format(new Date(commission.placedAt))}</td>
                  <td>{commission.source === 'coupon' ? 'Kód' : 'Odkaz'}</td>
                  <td>
                    {STATUS_LABEL[commission.status]}
                    {commission.clawback && <div className="muted small">Objednávka vrácena</div>}
                  </td>
                  <td className="num">{formatCzk(commission.baseMinor)}</td>
                  <td className="num">{formatCzk(commission.amountMinor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <h2>Výplaty</h2>
        {data.payouts.length === 0 ? (
          <p className="muted">Zatím se nic nevyplácelo.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Vznikla</th>
                <th className="num">Provizí</th>
                <th className="num">Odečet</th>
                <th className="num">Částka</th>
                <th>Stav</th>
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((payout) => (
                <tr key={payout.id}>
                  <td>{dateFmt.format(new Date(payout.createdAt))}</td>
                  <td className="num">{payout.commissionCount}</td>
                  <td className="num">
                    {payout.clawbackMinor === 0 ? '—' : `− ${formatCzk(payout.clawbackMinor)}`}
                  </td>
                  <td className="num">
                    <strong>{formatCzk(payout.totalMinor)}</strong>
                  </td>
                  <td>{payout.status === 'paid' ? 'Odesláno' : 'Připraveno'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
